import { Typography } from "@mui/material";
import {
  StyledFormControl,
  StyledFormInput,
  StyledFormLabelWrapper,
  StyledValidationText,
} from "Components/Shared/FormStyles";
import {
  Dropdown as DropdownInput,
  createLabelId,
} from "Components/Shared/Inputs/Dropdown";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import type { CodeListItem } from "Models/ICodeListDto";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { toArray } from "Utils/ArrayUtils";

type Props<FormType extends FieldValues, TransformedFieldValues = FormType> = {
  codeList: CodeListItem[];
  name: FieldPath<FormType>;
  formControlClass?: "long" | "medium" | "tiny";
  label?: string;
  control: Control<FormType, unknown, TransformedFieldValues>;
  disabled?: boolean;
  placeholder?: string;
  accessibilityLabel?: string;
  isMultiple?: boolean;
  // Fires after the field value updates (single-select only). Use for side effects on user selection —
  // e.g. resetting dependent fields when the choice invalidates them.
  onChange?: (value: string | number) => void;
};

export const Dropdown = <T extends object, V extends object>({
  codeList,
  name,
  control,
  label,
  disabled,
  formControlClass,
  placeholder,
  accessibilityLabel,
  isMultiple,
  onChange: onValueChange,
}: Props<T, V>) => (
  <Controller
    control={control}
    name={name}
    render={({
      field: { onChange, value, onBlur, ref },
      fieldState: { error },
    }) => {
      const options = codeList.map(({ name, id, code, disabled, icon }) => ({
        Name: name,
        Value: (id ?? code) as string | number,
        disabled,
        Icon: icon,
      }));

      const singleValue =
        value && codeList.some(({ id, code }) => id === value || code === value)
          ? (value as string | number)
          : undefined;

      return (
        <StyledFormControl
          className={`${formControlClass} ${!label && "no-label"}`}
        >
          {!!label && (
            <StyledFormLabelWrapper>
              <StyledInputLabel
                // htmlFor={name} //MUI select doesn't work this way, but the other way around. Label gets an id, and the select references it with labelId
                hasError={!!error}
                id={createLabelId(name)}
              >
                <Typography>{label}</Typography>
              </StyledInputLabel>
            </StyledFormLabelWrapper>
          )}
          <StyledFormInput>
            {isMultiple ? (
              <DropdownInput
                multiple
                value={toArray(value)}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                hasError={!!error}
                name={name}
                placeholder={placeholder}
                options={options}
                inputRef={ref}
                accessibilityLabel={accessibilityLabel}
              />
            ) : (
              <DropdownInput
                value={singleValue}
                onChange={(newValue) => {
                  onChange(newValue);
                  onValueChange?.(newValue);
                }}
                onBlur={onBlur}
                disabled={disabled}
                hasError={!!error}
                name={name}
                placeholder={placeholder}
                options={options}
                inputRef={ref}
                accessibilityLabel={accessibilityLabel}
              />
            )}
          </StyledFormInput>
          <StyledValidationText>
            {!!error && error.message}
          </StyledValidationText>
        </StyledFormControl>
      );
    }}
  />
);
